import { useCallback, useMemo } from "react"
import { useLiveQueryImmediate } from "@/db/hooks"
import { db } from "@/db/schema"
import { now } from "@/shared/utils/date/dateUtils"
import { trackSettingsChanged } from "@/shared/services/analyticsTrackers"

// ---------------------------------------------------------------------------
// useSettingLocal — device-specific settings (db.setting_local)
// ---------------------------------------------------------------------------

/**
 * Read/write a single device-local setting from `db.setting_local`.
 *
 * Same API as `useSetting`, but data is NOT synced via Dexie Cloud.
 * Use this for device-specific preferences like haptics, clipboard format, etc.
 *
 * @param id           Setting key, e.g. `"#haptics-enabled"`, `"#sidebar-width"`
 * @param defaultValue Value returned when no entry exists in DB
 *
 * @example
 * const { value, setValue, isLoading } = useSettingLocal("#haptics-enabled", false)
 */
export function useSettingLocal<T = string>(id: string, defaultValue: T) {
  const [row, , isLoading] = useLiveQueryImmediate(
    () => db.setting_local.get(id),
    ["setting-local", id],
    undefined,
  )

  const value: T = useMemo(() => {
    if (!row) return defaultValue
    if (row.value === undefined || row.value === "") return defaultValue
    try {
      return JSON.parse(row.value) as T
    } catch {
      return row.value as T
    }
  }, [row, defaultValue])

  const setValue = useCallback(
    async (v: T) => {
      const timestamp = now()
      const serialized = typeof v === "string" ? v : JSON.stringify(v)
      await db.setting_local.put({
        id,
        value: serialized,
        device: "local",
        createdAt: row?.createdAt ?? timestamp,
        createdBy: "local",
        updatedAt: timestamp,
        updatedBy: "local",
      })
      trackSettingsChanged(id, serialized)
    },
    [id, row?.createdAt],
  )

  const resetToDefault = useCallback(async () => {
    await db.setting_local.delete(id)
  }, [id])

  return useMemo(
    () => ({
      value,
      isLoading,
      setValue,
      resetToDefault,
    }),
    [value, isLoading, setValue, resetToDefault],
  )
}

/**
 * Device-local boolean setting using existence-based logic.
 */
export function useSettingLocalBoolean(id: string) {
  const [row, , isLoading] = useLiveQueryImmediate(
    () => db.setting_local.get(id),
    ["setting-local-bool", id],
    undefined,
  )

  const value = !!row

  const toggle = useCallback(async () => {
    if (row) {
      await db.setting_local.delete(id)
      trackSettingsChanged(id, "off")
    } else {
      await db.setting_local.put({
        id,
        value: "1",
        device: "local",
        createdAt: now(),
        createdBy: "local",
      })
      trackSettingsChanged(id, "on")
    }
  }, [id, row])

  const setValue = useCallback(
    async (v: boolean) => {
      if (v) {
        await db.setting_local.put({
          id,
          value: "1",
          device: "local",
          createdAt: now(),
          createdBy: "local",
        })
      } else {
        await db.setting_local.delete(id)
      }
    },
    [id],
  )

  return useMemo(
    () => ({
      value,
      isLoading,
      toggle,
      setValue,
    }),
    [value, isLoading, toggle, setValue],
  )
}
